import type {
  CompanyContact,
  CompanyContactsResponse,
  CompanyGeneralInfo,
  CompanyGeneralInfoResponse,
  CreateCompanyContactRequest,
  CreateSupplierCapitalEquipmentRequest,
  CreateSupplierCertificateRequest,
  CreateSupplierCertificateResponse,
  CreateSupplierDocumentRequest,
  CreateSupplierDocumentResponse,
  CreateSupplierManufacturingLocationRequest,
  ReplaceSupplierCapabilitiesRequest,
  ReplaceSupplierCertificatesRequest,
  ReplaceSupplierIndustriesRequest,
  ReplaceSupplierMeasurementCapabilitiesRequest,
  ReplaceSupplierSalesRequest,
  Supplier,
  SupplierCapabilitiesResponse,
  SupplierCapitalEquipment,
  SupplierCapitalEquipmentResponse,
  SupplierCertificatesResponse,
  SupplierDocumentsResponse,
  SupplierIndustriesResponse,
  SupplierManufacturingLocation,
  SupplierManufacturingLocationsResponse,
  SupplierMeasurementCapabilitiesResponse,
  SupplierPolicyResponse,
  SupplierQualityResponsesPayload,
  SupplierSalesResponse,
  SupplierWorkforceResponse,
  UpdateCompanyContactRequest,
  UpdateCompanyGeneralInfoRequest,
  UpdateSupplierCapitalEquipmentRequest,
  UpdateSupplierManufacturingLocationRequest,
  UpdateSupplierPolicyRequest,
  UpdateSupplierWorkforceRequest,
  UpsertSupplierQualitySectionRequest
} from '@tandemone/types'

import { api } from '@/api/client'

export async function getCompanyGeneralInfo(
  signal?: AbortSignal
): Promise<CompanyGeneralInfo> {
  const { data } = await api.get<CompanyGeneralInfoResponse>(
    '/v1/company/general-info',
    { signal }
  )
  return data.generalInfo
}

export async function updateCompanyGeneralInfo(
  body: UpdateCompanyGeneralInfoRequest
): Promise<CompanyGeneralInfo> {
  const { data } = await api.put<{ generalInfo: CompanyGeneralInfo }>(
    '/v1/company/general-info',
    body
  )
  return data.generalInfo
}

export async function getSupplierCapabilities(
  signal?: AbortSignal
): Promise<SupplierCapabilitiesResponse> {
  const { data } = await api.get<SupplierCapabilitiesResponse>(
    '/v1/supplier/capabilities',
    { signal }
  )
  return data
}

export async function replaceSupplierCapabilities(
  body: ReplaceSupplierCapabilitiesRequest
): Promise<SupplierCapabilitiesResponse> {
  const { data } = await api.put<SupplierCapabilitiesResponse>(
    '/v1/supplier/capabilities',
    body
  )
  return data
}

export async function getSupplierIndustries(
  signal?: AbortSignal
): Promise<SupplierIndustriesResponse> {
  const { data } = await api.get<SupplierIndustriesResponse>(
    '/v1/supplier/industries',
    { signal }
  )
  return data
}

export async function replaceSupplierIndustries(
  body: ReplaceSupplierIndustriesRequest
): Promise<SupplierIndustriesResponse> {
  const { data } = await api.put<SupplierIndustriesResponse>(
    '/v1/supplier/industries',
    body
  )
  return data
}

export async function getSupplierCertificates(
  signal?: AbortSignal
): Promise<SupplierCertificatesResponse> {
  const { data } = await api.get<SupplierCertificatesResponse>(
    '/v1/supplier/certificates',
    { signal }
  )
  return data
}

export async function replaceSupplierCertificates(
  body: ReplaceSupplierCertificatesRequest
): Promise<SupplierCertificatesResponse> {
  const { data } = await api.put<SupplierCertificatesResponse>(
    '/v1/supplier/certificates',
    body
  )
  return data
}

/**
 * appends a single cert from the post-onboarding Certificates tab. lands
 * unverified and enters the super review queue.
 */
export async function createSupplierCertificate(
  body: CreateSupplierCertificateRequest
): Promise<CreateSupplierCertificateResponse> {
  const { data } = await api.post<CreateSupplierCertificateResponse>(
    '/v1/supplier/certificates',
    body
  )
  return data
}

export async function getSupplierPolicy(
  signal?: AbortSignal
): Promise<SupplierPolicyResponse> {
  const { data } = await api.get<SupplierPolicyResponse>(
    '/v1/supplier/policies',
    { signal }
  )
  return data
}

export async function updateSupplierPolicy(
  body: UpdateSupplierPolicyRequest
): Promise<SupplierPolicyResponse> {
  const { data } = await api.put<SupplierPolicyResponse>(
    '/v1/supplier/policies',
    body
  )
  return data
}

export async function getSupplierWorkforce(
  signal?: AbortSignal
): Promise<SupplierWorkforceResponse> {
  const { data } = await api.get<SupplierWorkforceResponse>(
    '/v1/supplier/workforce',
    { signal }
  )
  return data
}

export async function updateSupplierWorkforce(
  body: UpdateSupplierWorkforceRequest
): Promise<SupplierWorkforceResponse> {
  const { data } = await api.put<SupplierWorkforceResponse>(
    '/v1/supplier/workforce',
    body
  )
  return data
}

export async function getSupplierBusinessContacts(
  signal?: AbortSignal
): Promise<CompanyContactsResponse> {
  const { data } = await api.get<CompanyContactsResponse>(
    '/v1/company/contacts',
    { signal }
  )
  return data
}

export async function createSupplierBusinessContact(
  body: CreateCompanyContactRequest
): Promise<CompanyContact> {
  const { data } = await api.post<{ contact: CompanyContact }>(
    '/v1/company/contacts',
    body
  )
  return data.contact
}

export async function updateSupplierBusinessContact(
  id: string,
  body: UpdateCompanyContactRequest
): Promise<CompanyContact> {
  const { data } = await api.put<{ contact: CompanyContact }>(
    `/v1/company/contacts/${id}`,
    body
  )
  return data.contact
}

export async function deleteSupplierBusinessContact(id: string): Promise<void> {
  await api.delete(`/v1/company/contacts/${id}`)
}

export async function getSupplierSales(
  signal?: AbortSignal
): Promise<SupplierSalesResponse> {
  const { data } = await api.get<SupplierSalesResponse>('/v1/supplier/sales', {
    signal
  })
  return data
}

export async function replaceSupplierSales(
  body: ReplaceSupplierSalesRequest
): Promise<SupplierSalesResponse> {
  const { data } = await api.put<SupplierSalesResponse>(
    '/v1/supplier/sales',
    body
  )
  return data
}

export async function getSupplierLocations(
  signal?: AbortSignal
): Promise<SupplierManufacturingLocationsResponse> {
  const { data } = await api.get<SupplierManufacturingLocationsResponse>(
    '/v1/supplier/locations',
    { signal }
  )
  return data
}

export async function createSupplierLocation(
  body: CreateSupplierManufacturingLocationRequest
): Promise<SupplierManufacturingLocation> {
  const { data } = await api.post<{ location: SupplierManufacturingLocation }>(
    '/v1/supplier/locations',
    body
  )
  return data.location
}

export async function updateSupplierLocation(
  id: string,
  body: UpdateSupplierManufacturingLocationRequest
): Promise<SupplierManufacturingLocation> {
  const { data } = await api.put<{ location: SupplierManufacturingLocation }>(
    `/v1/supplier/locations/${id}`,
    body
  )
  return data.location
}

export async function deleteSupplierLocation(id: string): Promise<void> {
  await api.delete(`/v1/supplier/locations/${id}`)
}

export async function getSupplierEquipment(
  signal?: AbortSignal
): Promise<SupplierCapitalEquipmentResponse> {
  const { data } = await api.get<SupplierCapitalEquipmentResponse>(
    '/v1/supplier/equipment',
    { signal }
  )
  return data
}

export async function createSupplierEquipment(
  body: CreateSupplierCapitalEquipmentRequest
): Promise<SupplierCapitalEquipment> {
  const { data } = await api.post<{ equipment: SupplierCapitalEquipment }>(
    '/v1/supplier/equipment',
    body
  )
  return data.equipment
}

export async function updateSupplierEquipment(
  id: string,
  body: UpdateSupplierCapitalEquipmentRequest
): Promise<SupplierCapitalEquipment> {
  const { data } = await api.put<{ equipment: SupplierCapitalEquipment }>(
    `/v1/supplier/equipment/${id}`,
    body
  )
  return data.equipment
}

export async function deleteSupplierEquipment(id: string): Promise<void> {
  await api.delete(`/v1/supplier/equipment/${id}`)
}

export async function getSupplierMeasurement(
  signal?: AbortSignal
): Promise<SupplierMeasurementCapabilitiesResponse> {
  const { data } = await api.get<SupplierMeasurementCapabilitiesResponse>(
    '/v1/supplier/measurement',
    { signal }
  )
  return data
}

export async function replaceSupplierMeasurement(
  body: ReplaceSupplierMeasurementCapabilitiesRequest
): Promise<SupplierMeasurementCapabilitiesResponse> {
  const { data } = await api.put<SupplierMeasurementCapabilitiesResponse>(
    '/v1/supplier/measurement',
    body
  )
  return data
}

export async function getSupplierQuality(
  signal?: AbortSignal
): Promise<SupplierQualityResponsesPayload> {
  const { data } = await api.get<SupplierQualityResponsesPayload>(
    '/v1/supplier/quality',
    { signal }
  )
  return data
}

export async function upsertSupplierQualitySection(
  body: UpsertSupplierQualitySectionRequest
): Promise<SupplierQualityResponsesPayload> {
  const { data } = await api.put<SupplierQualityResponsesPayload>(
    '/v1/supplier/quality/section',
    body
  )
  return data
}

export async function getSupplierDocuments(
  signal?: AbortSignal
): Promise<SupplierDocumentsResponse> {
  const { data } = await api.get<SupplierDocumentsResponse>(
    '/v1/supplier/documents',
    { signal }
  )
  return data
}

export async function createSupplierDocument(
  body: CreateSupplierDocumentRequest
): Promise<CreateSupplierDocumentResponse> {
  const { data } = await api.post<CreateSupplierDocumentResponse>(
    '/v1/supplier/documents',
    body
  )
  return data
}

export async function deleteSupplierDocument(id: string): Promise<void> {
  await api.delete(`/v1/supplier/documents/${id}`)
}

export async function getSupplierMe(signal?: AbortSignal): Promise<Supplier> {
  const { data } = await api.get<{ supplier: Supplier }>('/v1/supplier/me', {
    signal
  })
  return data.supplier
}

export async function setPublicProfile(enabled: boolean): Promise<Supplier> {
  const { data } = await api.put<{ supplier: Supplier }>(
    '/v1/supplier/me/public-profile',
    { enabled }
  )
  return data.supplier
}
